import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { useParams } from 'react-router-dom';
import { Button, Skeleton, Spinner } from 'ui';
import Feedback from 'components/Practice/RolePlay/PartnerGuide/Feedback';
import SuccessMessage from 'components/SuccessMessage';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import TryAgain from 'components/TryAgain';
import PreviewBanner from 'components/Practice/RolePlay/PartnerGuide/PreviewBanner';
import RolePlayTitle from 'components/Practice/RolePlay/PartnerGuide/RolePlayTitle';
import RequiredBanner from 'components/Practice/RolePlay/PartnerGuide/RequiredBanner';
import LearnerHeader from 'components/Practice/RolePlay/PartnerGuide/LearnerHeader';
import Scenario from 'components/Practice/RolePlay/PartnerGuide/Scenario';
import Goal from 'components/Practice/RolePlay/PartnerGuide/Goal';
import PartnerHeader from 'components/Practice/RolePlay/PartnerGuide/PartnerHeader';
import PartnerRole from 'components/Practice/RolePlay/PartnerGuide/PartnerRole';
import AdditionalContext from 'components/Practice/RolePlay/PartnerGuide/AdditionalContext';
import FeedbackHeader from 'components/Practice/RolePlay/PartnerGuide/FeedbackHeader';
import FeedbackCommentBox from 'components/Practice/RolePlay/PartnerGuide/FeedbackCommentBox';
import useFetch from '../../../shared/hooks/useFetch';

const PartnerGuide = () => {
  const { token, rolePlayId } = useParams();

  const isPreview = token === undefined;

  const {
    data = {},
    loading,
    error,
  } = useFetch(
    token
      ? routes.PRACTICE.ROLE_PLAY.PARTNER_GUIDE(token)
      : routes.PRACTICE.ROLE_PLAY.PARTNER_GUIDE_PREVIEW(rolePlayId),
  );

  const [commentValue, setCommentValue] = useState('');
  const [overallRating, setOverallRating] = useState(0);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [feedbackSubmissionFailed, setFeedbackSubmissionFailed] =
    useState(false);

  const [feedbackSubmissionSuccess, setFeedbackSubmissionSuccess] =
    useState(false);
  const [isPosting, setIsPosting] = useState(false);

  const onOverallChange = rating => {
    setOverallRating(rating);
  };
  const onCommentChange = e => {
    setCommentValue(e);
  };
  const onFeedbackChange = (id, rating) => {
    setCriteriaValues(prev => [
      ...prev.filter(value => value.criterionId !== id),
      { criterionId: id, score: rating },
    ]);
  };

  useEffect(() => {
    if (
      commentValue.trim() &&
      overallRating !== 0 &&
      criteriaValues.length === data.criteria.length
    ) {
      setDisableSubmit(false);
    } else if (
      !commentValue.trim() ||
      overallRating === 0 ||
      criteriaValues.length < data.criteria.length
    ) {
      setDisableSubmit(true);
    }
  }, [commentValue, overallRating, criteriaValues, data.criteria]);

  // Allow Page Error Boundary handles api errors
  useEffect(() => {
    if (error) {
      throw new Error(error);
    }
  }, [error]);

  const handleSubmit = async () => {
    setIsPosting(true);
    try {
      await request
        .postJSON(routes.PRACTICE.ROLE_PLAY.PARTNER_FEEDBACK(token), {
          feedback: commentValue,
          overallScore: overallRating,
          criteria: criteriaValues,
        })
        .then(res => {
          if (res.success) {
            setFeedbackSubmissionSuccess(true);
            setIsPosting(false);
          }
        });
    } catch (submitError) {
      setFeedbackSubmissionFailed(true);
      console.error(submitError);
      setFeedbackSubmissionFailed(true);
      setIsPosting(false);
    }
  };

  const shouldDisplayRequiredBanner =
    data?.required && !data?.overallScore && !feedbackSubmissionSuccess;

  return (
    <>
      {isPreview && <PreviewBanner isTemplate={data?.isTemplate} />}
      <div className="bg-white mb-70 bdrs-0 pl-58 pt-50 ws-pw pr-58 text-wrap pos-r">
        <RolePlayTitle loading={loading} title={data?.title} />
        {!loading && shouldDisplayRequiredBanner && (
          <RequiredBanner
            isPreview={isPreview}
            learnerName={data?.learnerName}
          />
        )}
        <div
          className={cn('d-f fd-c mw-700', {
            'mt-100': shouldDisplayRequiredBanner,
          })}
        >
          <LearnerHeader loading={loading} learnerName={data?.learnerName} />
          <div className="ml-20 d-f fd-c">
            <Scenario loading={loading} scenario={data?.scenario} />
            <Goal loading={loading} goal={data?.goal} />
          </div>
        </div>
        <div className="d-f fd-c mw-700">
          <PartnerHeader loading={loading} />
          <div className="ml-20 d-f fd-c">
            <PartnerRole loading={loading} partnerRole={data?.partnerRole} />
            {!loading && data.additionalContext && (
              <AdditionalContext additionalContext={data.additionalContext} />
            )}
          </div>
        </div>
        <div className="minh-250 mr-36">
          <FeedbackHeader />
          <hr className="border-top-1 border-midnight-20 mt-24" />
          {loading && <Skeleton className="mt-32" count={2} />}
          {!feedbackSubmissionSuccess && !loading && !data.overallScore && (
            <div>
              {data.criteria?.map(criterion => (
                <Feedback
                  key={criterion.id}
                  title={criterion.name}
                  description={criterion.description}
                  score={
                    criteriaValues.find(
                      value => value.criterionId === criterion.id,
                    )?.score
                  }
                  setScore={score => onFeedbackChange(criterion.id, score)}
                  disabled={isPreview}
                />
              ))}
              <Feedback
                title={t(
                  'practice.role_play.grading_criteria.overall_score_title',
                )}
                score={overallRating}
                setScore={score => onOverallChange(score)}
                disabled={isPreview}
              />
              <div className="w-100p d-f jc-e">
                <div className="d-f fd-c w-60p ai-fe">
                  <FeedbackCommentBox
                    onCommentChange={onCommentChange}
                    commentValue={commentValue}
                    disabled={isPreview || isPosting}
                  />
                  {!feedbackSubmissionFailed ? (
                    <Button
                      submit
                      disabled={disableSubmit || isPreview || isPosting}
                      customColor
                      className="mb-70"
                      ariaLabel={t(
                        'practice.role_play.partner_guide.submit_feedback',
                      )}
                      onClick={handleSubmit}
                    >
                      {isPosting && (
                        <Spinner color="darker" className="mr-16" />
                      )}
                      {t('practice.role_play.partner_guide.submit_feedback')}
                    </Button>
                  ) : (
                    <TryAgain
                      handleRetry={handleSubmit}
                      className="mb-70"
                      disabled={!commentValue.trim()}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {!loading && (feedbackSubmissionSuccess || data.overallScore) && (
            <SuccessMessage className="mt-32 h-100p jc-c" />
          )}
        </div>
      </div>
    </>
  );
};

export default props => <PartnerGuide {...props} />;
